import React from "react";
import "./KeepingUpWithTechnology.css";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";

export default function KeepingUpWithTechnology(): JSX.Element {
  return (
    <main className="kut-page">

      {/* HERO SECTION */}
      <section className="kut-hero">
        <div className="kut-hero-inner">
          <h1>Keep Up With Technology</h1>
          <p>
            A modern support and replacement program designed to keep your phone system
            updated, protected and fully maintained with flexible options to suit your business.
          </p>
        </div>
      </section>

      <div className="kut-container">

        {/* INTRO SECTION */}
        <section className="kut-section">
          <p className="kut-intro">
            KUWT is Jortel's premium technology care program designed to keep your phone and network systems running reliably, efficiently, and up to date without the unexpected costs, downtime, or maintenance headaches.
            Rather than reacting to problems as they arise, KUWT provides proactive protection, priority support, and built-in upgrade pathways that ensure your technology evolves with your business.
          </p>
        </section>

        {/* PURCHASE OPTIONS */}
        <section className="kut-section">
          <h2>Purchase Options</h2>

          <div className="kut-purchase">
            
            <div className="kut-purchase-copy">
            <p className="kut-purchase-note">
              KUWT (Basic Tier) is included as part of the 60-Month Interest-Free Repayment Plan.
            </p>
              <p>
                Whether you choose to purchase your devices outright or via our 60-Month Interest Free Repayment Plan,
                you have the option to enrol in Keep Up With Technology (KUWT).
              </p>
              <p>
                By opting into any KUWT tier, you are entitled to receive a brand new replacement system every five years
                on an ongoing, perpetual basis, with active services with Jortel. Your replacement system will be either
                an equivalent current-generation model or an upgraded model if your existing handsets are already the
                latest available at the time of renewal.
              </p>
            </div>
            <div className="kut-value-callout">
              <div className="kut-value-number">Illustrating the long-term value of KUWT</div>
              <div className="kut-value-text">
                <p>
                  Over a 40-year partnership with Jortel, a business could receive eight brand-new phone systems (one
                  every five years) while only paying for the initial 60-month interest free repayments on the first
                  system. Thereafter, the customer would simply continue on KUWT maintenance to remain fully upgraded
                  and supported.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* MAINTENANCE COMPARISON TABLE */}
        <section className="kut-section">
          <h2>Maintenance Plan Comparison</h2>

          <div className="kut-table-wrapper">
            <table className="kut-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Basic</th>
                  <th>Standard</th>
                  <th>Premium</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Hardware Failure", true, true, true],
                  ["Programming Failure", true, true, true],
                  ["Discounted Rates for Labour Exclusions", true, true, true],
                  ["1 Hour Complimentary Visit per Year", false, true, true],
                  ["Power Surges (UPS Required)", false, true, true],
                  ["Substitutions & Alterations", false, false, true],
                  ["Relocation (1 yr waiting period)", false, false, true],
                  ["24-48 hr Emergency Coverage", false, false, true],
                  ["Water Damage", false, false, true],
                  ["Physical Damage", false, false, true],
                ].map(([label, basic, standard, premium], i) => (
                  <tr key={i}>
                    <td>{label}</td>
                    <td>{basic ? <FaCheckCircle className="kut-yes" /> : <FaTimesCircle className="kut-no" />}</td>
                    <td>{standard ? <FaCheckCircle className="kut-yes" /> : <FaTimesCircle className="kut-no" />}</td>
                    <td>{premium ? <FaCheckCircle className="kut-yes" /> : <FaTimesCircle className="kut-no" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="kut-footnote">
            All coverage has a minimum 30-day waiting period unless otherwise stated.
          </p>
        </section>

        {/* FAQ */}
        <section className="kut-section">
          <a href="/docs/Jortel KUWT Critical Information Summary.pdf" target="_blank" rel="noopener noreferrer">
            Critical Information Summary
          </a>
        </section>

        <section className="kut-section">
          <h2>Frequently Asked Questions</h2>
          <div className="kut-faq-list">
            <details className="kut-faq-item">
              <summary>What is KUWT?</summary>
              <p>
                KUWT (Keep Up With Technology) is an ongoing maintenance program that keeps your system supported,
                optimised, and up-to-date with reduced downtime. It also lowers long-term costs via the exclusive
                benefit of replacement every 60 months. Replacement units are upgraded to newer models where applicable.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>Is KUWT compulsory when I purchase hardware?</summary>
              <p>
                No, it is optional. However, it is strongly recommended to ensure priority support, replacement
                coverage, and included maintenance services.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>Is there a lock-in contract?</summary>
              <p>
                KUWT operates like a subscription program. You can discontinue at any time; however, once cancelled,
                all benefits and coverage cease.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>What happens if my hardware fails?</summary>
              <p>
                Eligible hardware failures are covered, and replacement labour (if applicable) is discounted.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>Does KUWT include configuration changes?</summary>
              <p>
                Yes, minor programming faults and certain configuration changes are covered, depending on your KUWT tier.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>What about holiday messages and small changes?</summary>
              <p>
                These are included as part of KUWT, providing extra value and eliminating common ad-hoc service charges.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>What's not covered?</summary>
              <p>
                Physical damage, water damage, lightning surge without UPS, and third-party interference are excluded.
              </p>
            </details>
            <details className="kut-faq-item">
              <summary>Why is KUWT recommended at the time of purchase?</summary>
              <p>
                It ensures full protection, priority response, upgrade eligibility every 60 months, and better
                long-term value from day one.
              </p>
            </details>
          </div>
        </section>


      </div>

      <section className="kut-cta">
        <div className="kut-cta-inner">
          <h2>Stay Ahead With Modern Technology</h2>
          <p>Ensure your technology stays reliable with proactive replacements and maintenance.</p>
          <p>Ensure your technology stays reliable with proactive replacements and maintenance.</p>
          <a href="/contact" className="btn primary">Talk to an Expert</a>
        </div>
      </section>
    </main>
  );
}
